import { ScToast } from '@intelipost-js/secure-components';
import { Lodash, GraphQlClient, Http } from '@intelipost-js/secure-providers';
import { uploadFileToRemoteUrl } from '../../../store/commons';

export default {
  loadBillingTotalizers({ commit }, args) {
    commit('toggleLoadingTotalizers');
    return GraphQlClient.query(`
      query($dateSource: String, $dateRange: DateRangeInput, $logisticProviderId: Int, $warehouseIds: [Int]) {
        billingTotalizers(dateSource: $dateSource, dateRange: $dateRange, logisticProviderId: $logisticProviderId, warehouseIds: $warehouseIds) {
          field,
          value
        }
      }
    `, args).then(({ data }) => {
      const { billingTotalizers = [] } = data || {};
      commit('setTotalizerValues', billingTotalizers);
    });
  },
  loadBillingsByLogisticProvider({ commit }, args) {
    commit('setLoadingBillingsTable', true);
    return GraphQlClient.query(`
      query($dateSource: String, $dateRange: DateRangeInput, $warehouseIds: [Int]) {
        billingsByLogisticProvider(dateSource: $dateSource, dateRange: $dateRange, warehouseIds: $warehouseIds) {
          logisticProviderId,
          billingsCount,
          billingsTotalValue,
          shipmentOrdersCount,
          divergentBillingsCount,
          divergentBillingsTotalValue
        }
      }
    `, args).then(({ data }) => {
      const { billingsByLogisticProvider = [] } = data || {};
      commit('setBillingsByLogisticProvider', billingsByLogisticProvider);
    }).finally(() => commit('setLoadingBillingsTable', false));
  },
  loadBillingDetails({ commit }, args) {
    commit('setLoadingBillingDetails', true);
    return GraphQlClient.query(`
      query($dateSource: String, $dateRange: DateRangeInput, $logisticProviderId: Int, $warehouseIds: [Int]) {
        billingsDetail(dateSource: $dateSource, dateRange: $dateRange, logisticProviderId: $logisticProviderId, warehouseIds: $warehouseIds) {
          billingId,
          billingName,
          billingDueDate,
          billingWarehouseId,
          billingDateRange {
            start,
            end
          },
          billingTotalValue,
          shipmentOrdersCount,
          outOfMarginDivergences {
              shipmentOrdersCount,
              totalValue,
              greatherThanTMS,
              lowerThanTMS,
          },
          insideMarginDivergences {
              shipmentOrdersCount,
              totalValue,
              greatherThanTMS,
              lowerThanTMS,
          }
        }
      }
    `, args).then(({ data }) => {
      const { billingsDetail = [] } = data || {};
      commit('setLogisticProviderBillingDetails', billingsDetail);
    }).finally(() => commit('setLoadingBillingDetails', false));
  },
  loadSingleTemplate({ commit }, templateId) {
    commit('setLoadingBillingTemplates', true);
    return GraphQlClient.query(`
      query($id: ID!) {
        billingTemplate(id: $id) {
          id,
          name,
          headerRow,
          logisticProviderId,
          deliveryMethodId,
          created,
          columnsMapping {
            columnId,
            columnPreview,
            columnName,
            isPercent
          }
        }
      }
    `, { id: templateId }).then(({ data }) => {
      const { billingTemplate = [] } = data || {};
      commit('setSingleBillingTemplate', billingTemplate);
      return billingTemplate;
    }).finally(() => commit('setLoadingBillingTemplates', false));
  },
  loadClientTemplates({ commit }) {
    commit('setLoadingBillingTemplates', true);
    return GraphQlClient.query(`
      query {
        billingTemplates {
          id,
          name
          logisticProviderId,
          deliveryMethodId,
          created
        }
      }
    `).then(({ data }) => {
      const { billingTemplates = [] } = data || {};
      commit('setClientBillingTemplates', billingTemplates);
      return billingTemplates;
    }).finally(() => commit('setLoadingBillingTemplates', false));
  },
  uploadBillingFile({ commit }, args) {
    commit('setSavingBillingUpload', { saving: true, error: null });

    const { templateId: billingTemplateId, billingFile, ...rest } = args;

    const graphParams = {
      ...rest,
      billingTemplateId,
      fileName: billingFile.name,
      fileType: billingFile.type,
      fileSize: billingFile.size,
    };

    return GraphQlClient.mutation(`
      mutation (
        $overwriteId: String,
        $billingTemplateId: String!,
        $billingDueDate: Date!,
        $billingWarehouseId: Int,
        $fileName: String!,
        $fileType: String!,
        $fileSize: Int!,
        $onDuplicateAction: String
      ) {
        uploadBillingFile(
          overwriteId: $overwriteId,
          billingTemplateId: $billingTemplateId,
          billingDueDate: $billingDueDate,
          billingWarehouseId: $billingWarehouseId,
          fileName: $fileName,
          fileType: $fileType,
          fileSize: $fileSize,
          onDuplicateAction: $onDuplicateAction
        ) { url }
      }
    `, graphParams, { disableErrorToast: true }).then(({ data }) => {
      const { uploadBillingFile } = data || {};
      const uploadURL = uploadBillingFile.url;

      return uploadFileToRemoteUrl(uploadURL, billingFile)
        .finally(() => commit('setSavingBillingUpload', { saving: false, error: null }));
    }).catch(err => commit('setSavingBillingUpload', { saving: false, error: err.message }));
  },
  loadBillingFiles({ commit }) {
    commit('setLoadingBillingFiles', true);
    return GraphQlClient.query(`
      query {
        billingsFiles {
          source,
          filename,
          billingName,
          createdAt,
          finishedAt,
          status,
          url,
          errorRowNumber,
          errorColumnLetter,
          errorMappedValue,
          errorMessageKey,
          expirationDate,
          billingTemplateName,
        }
      }
    `).then(({ data }) => {
      const { billingsFiles = [] } = data || {};
      const [uploadFiles, extractionFiles] = Lodash.partition(billingsFiles, f => f.source === 'upload');
      commit('setBillingUploadFiles', uploadFiles);
      commit('setBillingExtractionFiles', extractionFiles);
    }).finally(() => commit('setLoadingBillingFiles', false));
  },
  exportBillingDetails({ commit }, args) {
    commit('setLoadingExtractDetail', args.billingId);
    return GraphQlClient.mutation(`
      mutation ($billingId: String!, $divergentsOnly: Boolean!) {
        exportBillingDetails(billingId: $billingId, divergentsOnly: $divergentsOnly) {
          message
        }
      }
    `, args).then(({ data }) => {
      const { exportBillingDetails = {} } = data || {};
      ScToast.success(exportBillingDetails.message);
    }).finally(() => commit('setLoadingExtractDetail', false));
  },
  previewBillingTemplate({ commit }, uploadForm) {
    commit('setLoadingBillingTemplatePreview', true);
    const columnsMapping = Object.entries(uploadForm.columns)
      .filter(([, v]) => !!v.letter)
      .map(([key, value]) => ({
        columnName: key,
        columnId: value.letter,
        isPercent: value.type === 'PERCENTAGE',
      }));

    const uploadData = new FormData();
    uploadData.append('file', uploadForm.previewFile);
    uploadData.append('config', JSON.stringify({ headerRow: uploadForm.rowNumber, columnsMapping }));

    const uploadUrl = `${process.env.GRAPHQL_URL}/api/billing-template/preview`;

    return Http.post(uploadUrl, uploadData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((result) => {
        if (result.data) {
          commit('setPreviewMap', result.data);
        }
      })
      .catch(() => ScToast.danger('Falha ao carregar o preview'))
      .finally(() => commit('setLoadingBillingTemplatePreview', false));
  },
  saveBillingTemplate({ commit, state }, uploadForm) {
    commit('setSavingBillingTemplate', { saving: true, error: null });

    const columnsMapping = Object.entries(uploadForm.columns)
      .filter(([k, v]) => !!v.letter && state.previewMap[k] !== 'VAZIO')
      .map(([key, value]) => ({
        columnName: key,
        columnId: value.letter,
        isPercent: value.type === 'PERCENTAGE',
        columnPreview: state.previewMap[key],
      }));

    const payload = {
      ...uploadForm,
      headerRow: uploadForm.rowNumber,
      logisticProviderId: uploadForm.lpAndDmInfo.logistic_provider_id,
      deliveryMethodId: uploadForm.lpAndDmInfo.id,
      columnsMapping,
    };

    return GraphQlClient.mutation(`
      mutation (
        $name: String!
        $headerRow: Int!
        $overwrite: Boolean,
        $logisticProviderId: Int!
        $deliveryMethodId: Int!
        $columnsMapping: [ColumnsMappingInputType]!
      ) {
        createBillingTemplate(
          name: $name
          headerRow: $headerRow
          overwrite: $overwrite,
          logisticProviderId: $logisticProviderId
          deliveryMethodId: $deliveryMethodId
          columnsMapping: $columnsMapping
        ) {
          id
        }
      }`, payload, { disableErrorToast: true })
      .then(() => commit('setSavingBillingTemplate', { saving: false, error: null }))
      .catch(err => commit('setSavingBillingTemplate', { saving: false, error: err.message }));
  },
  removeBillingTemplate({ dispatch }, templateId) {
    return GraphQlClient.mutation(`
      mutation($templateId: String!) {
        removeBillingTemplate(templateId: $templateId) {
          result
        }
      }
    `, { templateId }, { disableErrorToast: true })
      .catch(() => ScToast.danger('Falha ao remover o modelo'))
      .finally(() => dispatch('loadClientTemplates'));
  },
};



// WEBPACK FOOTER //
// ./node_modules/@intelipost-modules/secure-recon/features/billing/store/actions.js